
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTeams } from '@/hooks/useTeams';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { TeamLogoUpload } from './TeamLogoUpload';
import { toast } from 'sonner';
import { CertificationAgreementsModal } from '@/components/legal/CertificationAgreementsModal';
import { useCertificationAgreement } from '@/hooks/useCertificationAgreement';

interface CreateTeamFormProps {
  onSuccess: () => void;
}

export const CreateTeamForm: React.FC<CreateTeamFormProps> = ({ onSuccess }) => {
  const { createTeam, loadTeams } = useTeams();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  
  const { 
    showModal: showCertificationModal, 
    isSubmitting: isCertSubmitting,
    checkAndShowModal,
    submitAgreement,
    closeModal: closeCertificationModal
  } = useCertificationAgreement();
  
  const [formData, setFormData] = useState({
    team_name: '',
    team_description: '',
    sport_type: '',
    age_group: '',
    division: '',
    continent: '',
    state_province: '',
    city: '',
    location: '',
    team_logo: ''
  });

  const sportsOptions = [
    'Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis', 'Volleyball', 
    'Swimming', 'Track & Field', 'Wrestling', 'Golf', 'Hockey', 'Softball'
  ];

  const ageGroupOptions = [
    'Age 1', 'Age 2', 'Age 3', 'Age 4', 'Age 5', 'Age 6', 'Age 7', 'Age 8', 'Age 9', 'Age 10', 'Age 11', 'Age 12', 'Age 13',
    '13 and under', '14-17', '18-plus'
  ];

  const divisionOptions = [
    'Recreational', 'Competitive', 'Elite', 'Professional', 'Beginner', 
    'Intermediate', 'Advanced', 'Division 1', 'Division 2', 'Division 3'
  ];

  const continentOptions = [
    'North America', 'South America', 'Europe', 'Africa', 'Asia', 'Australia', 'Antarctica'
  ];

  const getStateProvinceOptions = (continent: string) => {
    switch (continent) {
      case 'North America':
        return [
          // United States
          'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
          'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
          'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
          'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
          'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
          'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
          'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
          'Wisconsin', 'Wyoming',
          // Canada
          'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 
          'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 
          'Quebec', 'Saskatchewan', 'Yukon',
          // Mexico
          'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 
          'Chihuahua', 'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 
          'Jalisco', 'Mexico', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 
          'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 
          'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
        ];
      case 'Australia':
        return [
          'Australian Capital Territory', 'New South Wales', 'Northern Territory', 
          'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
        ];
      case 'Europe':
        return [
          'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 
          'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 
          'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 
          'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'United Kingdom'
        ];
      case 'South America':
        return [
          'Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Ecuador', 'French Guiana', 
          'Guyana', 'Paraguay', 'Peru', 'Suriname', 'Uruguay', 'Venezuela'
        ];
      case 'Asia':
        return [
          'Afghanistan', 'Armenia', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Bhutan', 'Brunei', 
          'Cambodia', 'China', 'Georgia', 'India', 'Indonesia', 'Iran', 'Iraq', 'Israel', 
          'Japan', 'Jordan', 'Kazakhstan', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Lebanon', 'Malaysia', 
          'Maldives', 'Mongolia', 'Myanmar', 'Nepal', 'North Korea', 'Oman', 'Pakistan', 
          'Palestine', 'Philippines', 'Qatar', 'Saudi Arabia', 'Singapore', 'South Korea', 
          'Sri Lanka', 'Syria', 'Taiwan', 'Tajikistan', 'Thailand', 'Timor-Leste', 'Turkey', 
          'Turkmenistan', 'United Arab Emirates', 'Uzbekistan', 'Vietnam', 'Yemen'
        ];
      case 'Africa':
        return [
          'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cabo Verde', 
          'Cameroon', 'Central African Republic', 'Chad', 'Comoros', 'Congo', 'Democratic Republic of the Congo', 
          'Djibouti', 'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 
          'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Ivory Coast', 'Kenya', 'Lesotho', 
          'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 
          'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda', 'São Tomé and Príncipe', 
          'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 
          'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe'
        ];
      default:
        return [];
    }
  };

  const stateProvinceOptions = getStateProvinceOptions(formData.continent);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.team_name.trim()) {
      toast.error('Team name is required');
      return;
    }

    if (!formData.sport_type) {
      toast.error('Sport type is required');
      return;
    }

    const currentUser = supabaseUser || internalUser;
    if (!currentUser) {
      toast.error('You must be logged in to create a team');
      return;
    }

    // Check if user has accepted certification agreements
    const hasAccepted = await checkAndShowModal('team_owner');
    if (!hasAccepted) {
      // Modal will be shown, wait for user to accept
      return;
    }

    await proceedWithTeamCreation();
  };

  const proceedWithTeamCreation = async () => {
    const currentUser = supabaseUser || internalUser;
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Construct location from city, state, continent
      const locationParts = [formData.city, formData.state_province, formData.continent].filter(Boolean);
      const location = locationParts.join(', ');

      await createTeam({
        ...formData,
        location,
        created_by: currentUser.id
      });
      
      toast.success('Team created successfully!');
      
      // Refresh teams list to show the new team
      await loadTeams();
      
      onSuccess();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificationSubmit = async () => {
    const success = await submitAgreement('team_owner', true);
    if (success) {
      await proceedWithTeamCreation();
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Reset state/province when continent changes
      if (field === 'continent') {
        newData.state_province = '';
      }
      return newData;
    });
  };

  const handleLogoUpload = (logoUrl: string) => {
    setFormData(prev => ({ ...prev, team_logo: logoUrl }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Team Logo */}
      <div className="space-y-2">
        <Label className="text-gray-300">Team Logo</Label>
        <TeamLogoUpload
          currentLogo={formData.team_logo}
          onLogoUpload={handleLogoUpload}
        />
      </div>

      {/* Team Name */}
      <div className="space-y-2">
        <Label htmlFor="team_name" className="text-gray-300">
          Team Name *
        </Label>
        <Input
          id="team_name"
          type="text"
          value={formData.team_name}
          onChange={(e) => handleInputChange('team_name', e.target.value)}
          className="bg-gray-700 border-gray-600 text-white"
          placeholder="Enter team name"
          required
        />
      </div>

      {/* Sport Type */}
      <div className="space-y-2">
        <Label htmlFor="sport_type" className="text-gray-300">
          Sport *
        </Label>
        <Select value={formData.sport_type} onValueChange={(value) => handleInputChange('sport_type', value)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select sport" />
          </SelectTrigger>
          <SelectContent className="bg-gray-700 border-gray-600">
            {sportsOptions.map((sport) => (
              <SelectItem key={sport} value={sport} className="text-white hover:bg-gray-600">
                {sport}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Age Group */}
      <div className="space-y-2">
        <Label htmlFor="age_group" className="text-gray-300">
          Age Group
        </Label>
        <Select value={formData.age_group} onValueChange={(value) => handleInputChange('age_group', value)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select age group" />
          </SelectTrigger>
          <SelectContent className="bg-gray-700 border-gray-600">
            {ageGroupOptions.map((ageGroup) => (
              <SelectItem key={ageGroup} value={ageGroup} className="text-white hover:bg-gray-600">
                {ageGroup}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Division */}
      <div className="space-y-2">
        <Label htmlFor="division" className="text-gray-300">
          Division
        </Label>
        <Select value={formData.division} onValueChange={(value) => handleInputChange('division', value)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select division" />
          </SelectTrigger>
          <SelectContent className="bg-gray-700 border-gray-600">
            {divisionOptions.map((division) => (
              <SelectItem key={division} value={division} className="text-white hover:bg-gray-600">
                {division}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Team Description */}
      <div className="space-y-2">
        <Label htmlFor="team_description" className="text-gray-300">
          Team Description
        </Label>
        <Textarea
          id="team_description"
          value={formData.team_description}
          onChange={(e) => handleInputChange('team_description', e.target.value)}
          className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
          placeholder="Tell us about your team..."
        />
      </div>

      {/* Location Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="continent" className="text-gray-300">
            Continent
          </Label>
          <Select value={formData.continent} onValueChange={(value) => handleInputChange('continent', value)}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue placeholder="Select continent" />
            </SelectTrigger>
            <SelectContent className="bg-gray-700 border-gray-600">
              {continentOptions.map((continent) => (
                <SelectItem key={continent} value={continent} className="text-white hover:bg-gray-600">
                  {continent}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="state_province" className="text-gray-300">
            State/Province
          </Label>
          {formData.continent ? (
            <Select value={formData.state_province} onValueChange={(value) => handleInputChange('state_province', value)}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select state/province" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600 z-50">
                {stateProvinceOptions.map((stateProvince) => (
                  <SelectItem key={stateProvince} value={stateProvince} className="text-white hover:bg-gray-600">
                    {stateProvince}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-gray-400">
              Select continent first
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="city" className="text-gray-300">
            City
          </Label>
          <Input
            id="city"
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className="bg-gray-700 border-gray-600 text-white"
            placeholder="Enter city"
          />
        </div>
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        className="w-full bg-sports-green text-black hover:bg-green-600 font-semibold"
        disabled={loading}
      >
        {loading ? 'Creating Team...' : 'Create Team'}
      </Button>

      {/* Certification Agreements Modal */}
      <CertificationAgreementsModal
        open={showCertificationModal}
        roleType="team_owner"
        onSubmit={handleCertificationSubmit}
        onCancel={closeCertificationModal}
        isSubmitting={isCertSubmitting}
      />
    </form>
  );
};
